import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { globalStyles } from './theme';
import ProtectedRoute   from './components/ProtectedRoute';
import AppLayout        from './components/AppLayout';
import Login            from './pages/Login';
import Inspection       from './pages/Inspection';
import InspectionEdit   from './pages/InspectionEdit';
import InspectionList   from './pages/InspectionList';
import InspectionDetail from './pages/InspectionDetail';
import JobForm          from './pages/JobForm';
import NewEntryChoice   from './pages/NewEntryChoice';
import ViewDailySheetChoice from './pages/ViewDailySheetChoice';
import JobList          from './pages/JobList';
import JobSearch        from './pages/JobSearch';
import JobEdit          from './pages/JobEdit';
import JobView          from './pages/JobView';
import WalkInList       from './pages/WalkInList';
import WalkInForm       from './pages/WalkInForm';
import IncidentList     from './pages/IncidentList';
import IncidentForm     from './pages/IncidentForm';
import IncidentDetail   from './pages/IncidentDetail';
import ExcelUpload      from './pages/ExcelUpload';
import Dealerships      from './pages/Dealerships';
import Locations        from './pages/Locations';
import SiteLocations    from './pages/SiteLocations';
import Services         from './pages/Services';
import Reports          from './pages/Reports';
import ActivityLog      from './pages/ActivityLog';
import Staff            from './pages/Staff';
import Roles            from './pages/Roles';
import StaffInductionList   from './pages/StaffInductionList';
import StaffInductionForm   from './pages/StaffInductionForm';
import StaffInductionEdit   from './pages/StaffInductionEdit';
import StaffInductionDetail from './pages/StaffInductionDetail';
import EmployeeList   from './pages/EmployeeList';
import EmployeeForm   from './pages/EmployeeForm';
import EmployeeDetail from './pages/EmployeeDetail';
import AnnouncementList   from './pages/AnnouncementList';
import AnnouncementForm   from './pages/AnnouncementForm';
import AnnouncementDetail from './pages/AnnouncementDetail';
import AnnouncementView   from './pages/AnnouncementView';
import StaffRegistrationList   from './pages/StaffRegistrationList';
import StaffRegistrationDetail from './pages/StaffRegistrationDetail';
import StaffRegisterAuth       from './pages/StaffRegisterAuth';
import StaffRegisterForm       from './pages/StaffRegisterForm';
import InductionAccess      from './pages/InductionAccess';
import InductionAccessForm  from './pages/InductionAccessForm';

const Placeholder = ({ title }) => (
  <div style={{ padding:'40px', color:'var(--text)', fontFamily:'Inter,sans-serif' }}>
    <h2 style={{ marginBottom:'8px', color:'var(--blue)' }}>{title}</h2>
    <p style={{ color:'var(--muted)', fontSize:'14px' }}>This module is coming soon.</p>
  </div>
);

function HomeRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to="/jobs/new-entry" replace />;
}

const Layout = ({ children }) => (
  <ProtectedRoute>
    <AppLayout>{children}</AppLayout>
  </ProtectedRoute>
);

function App() {
  return (
    <>
      <style>{globalStyles}</style>
      <AuthProvider>
        <BrowserRouter future={{ v7_startTransition:true, v7_relativeSplatPath:true }}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<HomeRedirect />} />

            {/* Temporary staff induction access — public, no app layout or navigation */}
            <Route path="/induction-access"      element={<InductionAccess />} />
            <Route path="/induction-access/form" element={<InductionAccessForm />} />
            <Route path="/staff-register"        element={<StaffRegisterAuth />} />
            <Route path="/r"                     element={<StaffRegisterAuth />} />
            <Route path="/n/:token"              element={<AnnouncementView />} />
            <Route path="/staff-register/form"   element={<StaffRegisterForm />} />

            {/* Walk-ins */}
            <Route path="/walkins"      element={<Layout><WalkInList/></Layout>}/>
            <Route path="/walkins/new"  element={<Layout><WalkInForm/></Layout>}/>
            <Route path="/walkins/:id/edit" element={<Layout><WalkInForm/></Layout>}/>

            {/* Daily Sheet */}
            <Route path="/jobs/view"      element={<Layout><ViewDailySheetChoice/></Layout>}/>
            <Route path="/jobs"           element={<Layout><JobList/></Layout>}/>
            <Route path="/jobs/new-entry" element={<Layout><NewEntryChoice/></Layout>}/>
            <Route path="/jobs/new"       element={<Layout><JobForm/></Layout>}/>
            <Route path="/jobs/search"    element={<Layout><JobSearch/></Layout>}/>
            <Route path="/jobs/upload"    element={<Layout><ExcelUpload/></Layout>}/>
            <Route path="/jobs/:id/edit"  element={<Layout><JobEdit/></Layout>}/>
            <Route path="/jobs/:id/view"  element={<Layout><JobView/></Layout>}/>
            <Route path="/jobs/:id"       element={<Layout><JobEdit/></Layout>}/>

            {/* Inspections */}
            <Route path="/inspections"          element={<Layout><InspectionList/></Layout>}/>
            <Route path="/inspection/new"       element={<Layout><Inspection/></Layout>}/>
            <Route path="/inspection/:id"       element={<Layout><InspectionDetail/></Layout>}/>
            <Route path="/inspection/:id/edit"  element={<Layout><InspectionEdit/></Layout>}/>

            {/* CS Staff */}
            <Route path="/staff"          element={<Layout><EmployeeList/></Layout>}/>
            <Route path="/staff/new"      element={<Layout><EmployeeForm/></Layout>}/>
            <Route path="/staff/:id"      element={<Layout><EmployeeDetail/></Layout>}/>
            <Route path="/staff/:id/edit" element={<Layout><EmployeeForm/></Layout>}/>
            <Route path="/announcements"           element={<Layout><AnnouncementList/></Layout>}/>
            <Route path="/announcements/new"       element={<Layout><AnnouncementForm/></Layout>}/>
            <Route path="/announcements/:id"       element={<Layout><AnnouncementDetail/></Layout>}/>
            <Route path="/staff-registrations"     element={<Layout><StaffRegistrationList/></Layout>}/>
            <Route path="/staff-registrations/:id" element={<Layout><StaffRegistrationDetail/></Layout>}/>

            {/* HR */}
            <Route path="/admin-staff" element={<Layout><Staff/></Layout>}/>
            <Route path="/timesheets"  element={<Layout><Placeholder title="⏱ Timesheets"/></Layout>}/>
            <Route path="/reports"     element={<Layout><Reports/></Layout>}/>
            <Route path="/activity"    element={<Layout><ActivityLog/></Layout>}/>
            <Route path="/staff-induction"       element={<Layout><StaffInductionList/></Layout>}/>
            <Route path="/staff-induction/new"   element={<Layout><StaffInductionForm/></Layout>}/>
            <Route path="/staff-induction/:id"   element={<Layout><StaffInductionDetail/></Layout>}/>
            <Route path="/staff-induction/:id/edit" element={<Layout><StaffInductionEdit/></Layout>}/>

            {/* Incidents */}
            <Route path="/incidents"      element={<Layout><IncidentList/></Layout>}/>
            <Route path="/incidents/new"  element={<Layout><IncidentForm/></Layout>}/>
            <Route path="/incidents/:id"  element={<Layout><IncidentDetail/></Layout>}/>

            {/* Settings */}
            <Route path="/dealerships"    element={<Layout><Dealerships/></Layout>}/>
            <Route path="/locations"      element={<Layout><Locations/></Layout>}/>
            <Route path="/site-locations" element={<Layout><SiteLocations/></Layout>}/>
            <Route path="/services"       element={<Layout><Services/></Layout>}/>
            <Route path="/roles"          element={<Layout><Roles/></Layout>}/>

            {/* Legacy redirects — old URLs still work */}
            <Route path="/owner/*"   element={<Navigate to="/" replace />}/>
            <Route path="/manager/*" element={<Navigate to="/" replace />}/>
            <Route path="/employee/*" element={<Navigate to="/" replace />}/>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </>
  );
}

export default App;
